import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, ChevronDown, Zap, Pencil } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";
import ProductPicker from "@/components/ProductPicker";
import DeleteButton from "@/components/DeleteButton";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_MAP, prettyCat, buildProductName } from "@/lib/catalog";

const UNITS = ["pcs", "sqft", "boxes"];

const EMPTY = {
  date: new Date().toISOString().slice(0, 10),
  category: "terrazzo_tiles", variant: "", color: "", size: "", custom_size: "",
  product_name: "", unit: "pcs",
  quantity: "", notes: "",
};

export default function Production() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/production").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (keepOpen = false) => {
    if (!form.quantity) { toast.error("Quantity required"); return; }
    try {
      await api.post("/production", {
        date: form.date,
        product_category: form.category,
        product_name: form.product_name || buildProductName(form),
        variant: form.variant || null,
        color: form.color || null,
        size: form.size || null,
        custom_size: form.custom_size || null,
        quantity: parseFloat(form.quantity),
        unit: form.unit,
        notes: form.notes || null,
      });
      toast.success("Production entry saved");
      if (keepOpen) {
        setForm({ ...form, quantity: "", notes: "" });
      } else {
        setOpen(false);
        setForm({ ...form, quantity: "", notes: "" });
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };

  const [editing, setEditing] = useState(null);
  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/production/${editing.id}`, {
        date: editing.date, product_category: editing.product_category,
        product_name: editing.product_name, quantity: parseFloat(editing.quantity),
        unit: editing.unit, notes: editing.notes || null,
        variant: editing.variant, color: editing.color, size: editing.size, custom_size: editing.custom_size,
      });
      toast.success("Updated"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Recent unique products for one-tap re-entry
  const recentTemplates = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const r of rows) {
      const key = `${r.product_category}|${r.variant || ""}|${r.color || ""}|${r.size || ""}|${r.custom_size || ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
      if (out.length >= 6) break;
    }
    return out;
  }, [rows]);

  const applyTemplate = (t) => {
    setForm({
      ...EMPTY,
      date: new Date().toISOString().slice(0, 10),
      category: t.product_category,
      variant: t.variant || "",
      color: t.color || "",
      size: t.size || "",
      custom_size: t.custom_size || "",
      product_name: t.product_name || "",
      unit: t.unit || "pcs",
    });
    setOpen(true);
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Log</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter font-black">Production</h1>
          </div>
          <div className="flex gap-2 items-center">
            <ExportButton module="production" />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-production" className="bg-[#1C1C1A] hover:bg-black text-white"><Plus className="w-4 h-4 mr-2" /> New entry</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Log production</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs">Date</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                  </div>
                </div>
                <div className="mt-3">
                  <ProductPicker value={form} onChange={setForm} testidPrefix="prod" />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Quantity</Label>
                    <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="prod-qty" className="h-11 text-base" />
                  </div>
                  <div>
                    <Label className="text-xs">Unit</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full justify-between mt-2">{form.unit}<ChevronDown className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {UNITS.map(u => <DropdownMenuItem key={u} onClick={() => setForm({ ...form, unit: u })}>{u}</DropdownMenuItem>)}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Notes</Label>
                    <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => save(false)} data-testid="prod-save" className="mt-4 bg-[#1C1C1A] hover:bg-black text-white h-11 flex-1">Save entry</Button>
                  <Button onClick={() => save(true)} variant="outline" data-testid="prod-save-add" className="mt-4 h-11">Save & add another</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Quick re-entry chips */}
        {recentTemplates.length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A] mb-2 flex items-center gap-1"><Zap className="w-3 h-3" /> Repeat recent</div>
            <div className="flex flex-wrap gap-2">
              {recentTemplates.map((t, i) => (
                <button
                  key={i} type="button" onClick={() => applyTemplate(t)}
                  data-testid={`repeat-${i}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#D8D8D5] bg-white hover:bg-[#1C1C1A] hover:text-white transition-colors"
                >
                  {t.product_name || prettyCat(t.product_category)}
                </button>
              ))}
            </div>
          </div>
        )}

        <Card className="mt-6 border border-[#D8D8D5] shadow-none rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Worker</TableHead>
                <TableHead>Notes</TableHead>
                {user?.role === "admin" && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={user?.role === "admin" ? 7 : 6} className="text-center text-[#6E6E6A] py-8">No entries yet</TableCell></TableRow>}
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`prod-row-${r.id}`}>
                  <TableCell>{r.date}</TableCell>
                  <TableCell><span className="uppercase text-[10px] tracking-[0.2em] text-[#6E6E6A]">{prettyCat(r.product_category)}</span></TableCell>
                  <TableCell className="text-sm">{r.product_name || "—"}</TableCell>
                  <TableCell className="font-medium">{r.quantity} {r.unit}</TableCell>
                  <TableCell>{r.worker_name}</TableCell>
                  <TableCell className="text-xs text-[#6E6E6A] max-w-xs truncate">{r.notes}</TableCell>
                  {user?.role === "admin" && (
                    <TableCell><div className="flex gap-1"><Button variant="ghost" size="icon" onClick={() => setEditing({ ...r })} data-testid={`edit-prod-${r.id}`} className="h-7 w-7"><Pencil className="w-3.5 h-3.5" /></Button><DeleteButton endpoint={`/production/${r.id}`} label="production entry" onDone={load} testid={`del-prod-${r.id}`} /></div></TableCell>
                  )}
                  {user?.role === "supervisor" && (
                    <TableCell><Button variant="ghost" size="icon" onClick={() => setEditing({ ...r })} data-testid={`edit-prod-${r.id}`} className="h-7 w-7"><Pencil className="w-3.5 h-3.5" /></Button></TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit production entry</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date</Label><Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
              <div><Label className="text-xs">Category</Label><Input value={editing.product_category} onChange={(e) => setEditing({ ...editing, product_category: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">Product name</Label><Input value={editing.product_name || ""} onChange={(e) => setEditing({ ...editing, product_name: e.target.value })} /></div>
              <div><Label className="text-xs">Quantity</Label><Input type="number" value={editing.quantity} onChange={(e) => setEditing({ ...editing, quantity: e.target.value })} data-testid="prod-edit-qty" /></div>
              <div><Label className="text-xs">Unit</Label><Input value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">Notes</Label><Input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
            </div>
          )}
          <Button onClick={saveEdit} data-testid="prod-edit-save" className="mt-4 bg-[#1C1C1A] hover:bg-black text-white">Save changes</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
